import React, { useEffect, useState, useCallback } from 'react';
import { getClients, getClientDepartments, getEmployeeTimesheetOverview, createEmployeeTimesheets, updateEmployeeTimesheet, getEmployeeTimesheets, sendTimesheetPdf, bulkSendTimesheetPdfs, sendTimesheetWhatsapp, bulkSendTimesheetWhatsapp } from '../api';
import { DatePicker, Select, Table, Spin, Row, Col, Button, Modal, InputNumber, message, Card, Tooltip } from 'antd';
import { List as VirtualList } from 'react-window';
import moment from 'moment';
import { Row as AntRow, Col as AntCol } from 'antd';
import { FaWhatsapp, FaEnvelope } from 'react-icons/fa';

const { Option } = Select;

const EmployeeTimesheets = () => {
  const [clients, setClients] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(moment());
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [editingEmployee, setEditingEmployee] = useState({
    id: null,
    name: '',
    start_date: null,
    end_date: null
  });
  const [timesheetGrid, setTimesheetGrid] = useState([{
    date: '',
    dateDisplay: '',
    hours_worked: '',
    id: null
  }]);
  const [modalVisible, setModalVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  const [whatsappSending, setWhatsappSending] = useState(false);
  const [bulkWhatsappSending, setBulkWhatsappSending] = useState(false);

  useEffect(() => {
    getClients().then(res => {
      if (res.success) setClients(res.data);
    });
  }, []);

  useEffect(() => {
    if (selectedClient) {
      getClientDepartments(selectedClient).then(res => {
        if (res.success) setDepartments(res.data);
      });
    } else {
      setDepartments([]);
      setSelectedDepartment(null);
    }
  }, [selectedClient]);

  const fetchData = useCallback(() => {
    if (!selectedClient) return;
    setLoading(true);
    getEmployeeTimesheetOverview({
      client_id: selectedClient,
      client_department_id: selectedDepartment,
      month: selectedMonth.format('YYYY-MM'),
    }).then(res => {
      setLoading(false);
      const assignments = Array.isArray(res?.data.data?.assignments) ? res.data.data.assignments : [];
      const timesheets = Array.isArray(res?.data.data?.timesheets) ? res.data.data.timesheets : [];
      // Map assignments to table rows
      const tableData = assignments.map(a => {
        const tsList = timesheets.filter(ts => ts.employee_assignment_id === a.id);
        const total_hours = tsList.reduce((sum, ts) => sum + Number(ts.hours_worked || 0), 0);
        return {
          id: a.id,
          emp_code: a.employee_details?.emp_code || '',
          name: a.employee_details?.name || '',
          client_name: a.client?.name || '',
          department_name: a.client_department?.name || '',
          total_hours,
          // Add start_date and end_date from the assignment
          start_date: a.start_date,
          end_date: a.end_date,
          // Include the original assignment for reference
          assignment: a
        };
      });
      setData(tableData);
    });
  }, [selectedClient, selectedDepartment, selectedMonth]);

  useEffect(() => {
    if (selectedMonth && selectedClient) fetchData();
  }, [fetchData, selectedMonth, selectedClient]);

  const openTimesheetModal = async (employeeRow) => {
    console.log('Opening timesheet for employee:', employeeRow);
    
    // Use assignment data if available, otherwise fall back to employeeRow
    const assignment = employeeRow.assignment || employeeRow;
    
    if (!assignment.start_date || !assignment.end_date) {
      console.error('Missing date range in assignment:', assignment);
      message.error('This assignment is missing a valid date range. Please check the assignment details.');
      return;
    }

    console.log('Using date range:', {
      start_date: assignment.start_date,
      end_date: assignment.end_date,
      id: assignment.id
    });

    try {
      const startDate = moment(assignment.start_date);
      const endDate = moment(assignment.end_date);

      if (!startDate.isValid() || !endDate.isValid()) {
        throw new Error('Invalid date format in assignment');
      }

      // Fetch timesheets for the assignment's date range
      const res = await getEmployeeTimesheets({
        employee_assignment_id: assignment.id,
        start_date: startDate.format('YYYY-MM-DD'),
        end_date: endDate.format('YYYY-MM-DD'),
      });

      const timesheets = Array.isArray(res?.data) ? res.data : [];
      const grid = [];

      // Generate grid for the assignment's date range
      const currentDate = startDate.clone();
      while (currentDate.isSameOrBefore(endDate)) {
        const dateStr = currentDate.format('YYYY-MM-DD');
        const ts = timesheets.find(t => t.date === dateStr);
        grid.push({
          date: dateStr,
          dateDisplay: currentDate.format('DD MMM YYYY'),
          hours_worked: ts?.hours_worked || '',
          id: ts?.id, // for update
        });
        currentDate.add(1, 'day');
      }
      
      console.log(`Generated timesheet grid with ${grid.length} days`);
      setEditingEmployee({
        ...employeeRow,
        start_date: assignment.start_date,
        end_date: assignment.end_date
      });
      setTimesheetGrid(grid);
      setModalVisible(true);
    } catch (error) {
      console.error('Error opening timesheet modal:', error);
      message.error(`Failed to open timesheet: ${error.message}`);
    }
  };

  const handleGridChange = (idx, value) => {
    setTimesheetGrid(prev => prev.map((row, i) => i === idx ? { ...row, hours_worked: value } : row));
  };

  const saveTimesheet = async () => {
    setSaving(true);
    try {
      for (const row of timesheetGrid) {
        if (row.id) {
          await updateEmployeeTimesheet(row.id, {
            hours_worked: row.hours_worked,
            remarks: '',
          });
        } else if (row.hours_worked) {
          await createEmployeeTimesheets({
            employee_assignment_id: editingEmployee.id,
            date: row.date,
            hours_worked: row.hours_worked,
            remarks: '',
          });
        }
      }
      message.success('Timesheet saved!');
      setModalVisible(false);
      setEditingEmployee(null);
      fetchData();
    } catch (err) {
      message.error('Failed to save timesheet');
    } finally {
      setSaving(false);
    }
  };

  // Send individual timesheet PDF
  const handleSendTimesheetPdf = async (record) => {
    setSending(record.id);
    try {
      const res = await sendTimesheetPdf(record.emp_code, selectedMonth.format('YYYY-MM'));
      if (res.success) {
        message.success('Timesheet PDF sent to ' + record.name);
      } else {
        message.error(res.message || 'Failed to send timesheet PDF');
      }
    } catch (err) {
      message.error('Failed to send timesheet PDF');
    } finally {
      setSending(false);
    }
  };

  // Send all timesheet PDFs
  const handleBulkSendTimesheetPdfs = async () => {
    setBulkSending(true);
    try {
      const res = await bulkSendTimesheetPdfs(selectedMonth.format('YYYY-MM'));
      if (res.success) {
        message.success(res.message || 'Timesheets sent');
      } else {
        message.error(res.message || 'Failed to send timesheets');
      }
    } catch (err) {
      message.error('Failed to send timesheets');
    } finally {
      setBulkSending(false);
    }
  };

  // Send timesheet via WhatsApp
  const handleSendTimesheetWhatsapp = async (record) => {
    setWhatsappSending(record.id);
    try {
      const res = await sendTimesheetWhatsapp(record.emp_code, selectedMonth.format('YYYY-MM'));
      if (res.success) {
        message.success('Timesheet sent via WhatsApp to ' + record.name);
      } else {
        message.error(res.message || 'Failed to send via WhatsApp');
      }
    } catch (err) {
      message.error('Failed to send via WhatsApp');
    } finally {
      setWhatsappSending(false);
    }
  };

  // Bulk send timesheets via WhatsApp
  const handleBulkSendTimesheetWhatsapp = async () => {
    setBulkWhatsappSending(true);
    try {
      const res = await bulkSendTimesheetWhatsapp(selectedMonth.format('YYYY-MM'));
      if (res.success) {
        message.success(res.message || 'Timesheets sent via WhatsApp');
      } else {
        message.error(res.message || 'Failed to send timesheets via WhatsApp');
      }
    } catch (err) {
      message.error('Failed to send timesheets via WhatsApp');
    } finally {
      setBulkWhatsappSending(false);
    }
  };

  const columns = [
    { title: 'Employee Code', dataIndex: 'emp_code', key: 'emp_code', width: 120 },
    { title: 'Name', dataIndex: 'name', key: 'name', width: 180 },
    { title: 'Total Hours', dataIndex: 'total_hours', key: 'total_hours', width: 120 },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <>
          <Button onClick={() => openTimesheetModal(record)} size="small" type={record.total_hours === 0 ? 'primary' : 'default'} style={{ marginRight: 8 }}>
            {record.total_hours === 0 ? 'Add' : 'Update'}
          </Button>
          <Tooltip title="Send Email">
            <Button onClick={() => handleSendTimesheetPdf(record)} size="small" loading={sending === record.id} style={{ marginRight: 8 }}>
              <FaEnvelope style={{ color: '#1890ff', fontSize: 16 }} />
            </Button>
          </Tooltip>
          <Tooltip title="Send WhatsApp">
            <Button onClick={() => handleSendTimesheetWhatsapp(record)} size="small" loading={whatsappSending === record.id} icon={<FaWhatsapp style={{ color: '#25D366', fontSize: 18 }} />} />
          </Tooltip>
        </>
      ),
      width: 320,
    },
  ];

  // Virtualized Table Body
  const VirtualTable = (props) => {
    const { columns, dataSource, rowHeight = 48 } = props;
    const safeData = Array.isArray(dataSource) ? dataSource : [];
    return (
      <VirtualList
        height={600}
        itemCount={safeData.length}
        itemSize={rowHeight}
        width="100%"
      >
        {({ index, style }) => (
          <tr style={style} key={safeData[index]?.key || index}>
            {columns.map(col => (
              <td key={col.key || col.dataIndex} style={{ width: col.width }}>{safeData[index]?.[col.dataIndex]}</td>
            ))}
          </tr>
        )}
      </VirtualList>
    );
  };

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col>
          <Select
            allowClear
            placeholder="Select Client"
            style={{ width: 200 }}
            value={selectedClient}
            onChange={val => setSelectedClient(val)}
          >
            {clients.map(client => (
              <Option key={client.id} value={client.id}>{client.name}</Option>
            ))}
          </Select>
        </Col>
        <Col>
          <Select
            allowClear
            placeholder="Select Department"
            style={{ width: 200 }}
            value={selectedDepartment}
            onChange={val => setSelectedDepartment(val)}
            disabled={!selectedClient}
          >
            {departments.map(dep => (
              <Option key={dep.id} value={dep.id}>{dep.name}</Option>
            ))}
          </Select>
        </Col>
        <Col>
          <DatePicker
            picker="month"
            value={selectedMonth}
            onChange={val => setSelectedMonth(val)}
            style={{ width: 160 }}
          />
        </Col>
        <Col>
          <Button type="primary" onClick={fetchData} loading={loading} disabled={!selectedClient}>
            Refresh
          </Button>
        </Col>
        <Col>
          <Tooltip title="Send All Email">
            <Button type="dashed" onClick={handleBulkSendTimesheetPdfs} loading={bulkSending} disabled={!selectedClient} icon={<FaEnvelope style={{ color: '#1890ff', fontSize: 16 }} />} />
          </Tooltip>
        </Col>
        <Col>
          <Tooltip title="Send All WhatsApp">
            <Button type="dashed" onClick={handleBulkSendTimesheetWhatsapp} loading={bulkWhatsappSending} disabled={!selectedClient} icon={<FaWhatsapp style={{ color: '#25D366', fontSize: 18 }} />} />
          </Tooltip>
        </Col>
      </Row>
      <Spin spinning={loading} tip="Loading timesheets...">
        {selectedClient ? (
          <Table
            columns={columns}
            dataSource={data}
            rowKey={record => record.id}
            pagination={false}
            scroll={{ x: 1200, y: 600 }}
          />
        ) : (
          <div style={{ padding: 32, textAlign: 'center', color: '#888' }}>
            Please select a client to view timesheets.
          </div>
        )}
      </Spin>
      <Modal
        open={modalVisible}
        title={editingEmployee ? `Edit Timesheet: ${editingEmployee.name}` : ''}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={1000}
        destroyOnClose
      >
        <div style={{ padding: 6, maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' }}>
          {editingEmployee ? (
            <>
              <div style={{ textAlign: 'right', marginBottom: 8 }}>
                <Button onClick={() => setModalVisible(false)} style={{ marginRight: 8 }} size="small">
                  Cancel
                </Button>
                <Button type="primary" onClick={saveTimesheet} loading={saving} size="small">
                  Save
                </Button>
              </div>
              <div style={{ marginBottom: 8, fontWeight: 500, textAlign: 'center', fontSize: 15 }}>
                {editingEmployee.start_date && editingEmployee.end_date ? 
                  `${moment(editingEmployee.start_date).format('DD MMM YYYY')} - ${moment(editingEmployee.end_date).format('DD MMM YYYY')}` : 
                  'Select Date Range'}
              </div>
              <AntRow gutter={[4, 4]}>
                {timesheetGrid.map((row, idx) => (
                  <AntCol xs={8} sm={6} md={4} lg={3} xl={2} key={row.date} style={{ paddingBottom: 4 }}>
                    <Card size="small" bordered={false} style={{ textAlign: 'center', margin: 0, padding: 0, minWidth: 70, boxShadow: '0 1px 2px #eee' }} bodyStyle={{ padding: 6 }}>
                      <div style={{ fontWeight: 500, fontSize: 13, marginBottom: 2 }}>D{idx + 1}</div>
                      <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>{row.dateDisplay || row.date?.slice(-2) || ''}</div>
                      <InputNumber
                        min={0}
                        max={24}
                        value={row.hours_worked}
                        onChange={val => handleGridChange(idx, val)}
                        style={{ width: 50, fontSize: 12 }}
                        size="small"
                      />
                      <div style={{ fontSize: 10, color: '#aaa', marginTop: 1 }}>h</div>
                    </Card>
                  </AntCol>
                ))}
              </AntRow>
            </>
          ) : null}
        </div>
      </Modal>
    </div>
  );
};

export default EmployeeTimesheets; 